import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Calendar,
  CreditCard,
  Clock,
  ChevronRight,
  Loader2,
  CalendarDays,
  ClipboardList,
  CheckCircle,
  User,
  MapPin,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useMyClasses } from '../hooks/useStudent';
import { scheduleService, ScheduleEntry } from '@/features/calendar/services/scheduleService';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FinanceWidget } from '@/shared/components/dashboard';

// Helper to format date as YYYY-MM-DD
const formatLocalDate = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const tenantId = user?.tenantId;

  // Fetch classes (current semester)
  const { data: classes = [], isLoading: isLoadingClasses } = useMyClasses({});

  // Fetch today's schedule
  const today = new Date();
  const dayOfWeek = today.getDay();
  const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const monday = new Date(today);
  monday.setDate(diff);
  const weekStart = formatLocalDate(monday);

  const { data: scheduleData, isLoading: isLoadingSchedule } = useQuery({
    queryKey: ['student-schedule', weekStart],
    queryFn: () => scheduleService.getStudentSchedule(weekStart),
    staleTime: 5 * 60 * 1000,
  });

  // Calculate stats
  const stats = useMemo(() => {
    if (!classes.length) return null;

    const totalClasses = classes.length;
    const avgAttendance = classes.reduce((sum, c) => sum + c.attendanceRate, 0) / totalClasses;
    const pendingAssignments = classes.reduce((sum, c) => sum + c.pendingAssignments, 0);

    // Calculate average grade
    const classesWithGrades = classes.filter((c) => c.gradeAverage !== null);
    const avgGrade = classesWithGrades.length > 0
      ? classesWithGrades.reduce((sum, c) => sum + (c.gradeAverage || 0), 0) / classesWithGrades.length
      : null;

    return {
      totalClasses,
      avgAttendance: Math.round(avgAttendance),
      pendingAssignments,
      avgGrade,
    };
  }, [classes]);

  // Get today's classes
  const todayClasses = useMemo(() => {
    if (!scheduleData) return [];

    const todayDayOfWeek = today.getDay();
    const todayDateStr = formatLocalDate(today);

    const entries = Array.isArray(scheduleData) ? scheduleData : (scheduleData as any).entries || [];

    return entries
      .filter((entry: ScheduleEntry) => {
        if (entry.dayOfWeek !== todayDayOfWeek) return false;

        // Check if there's a session for today
        if (entry.sessions && entry.sessions.length > 0) {
          return entry.sessions.some((s) => {
            const sessionDateStr = typeof s.date === 'string'
              ? s.date.split('T')[0]
              : formatLocalDate(new Date(s.date));
            return sessionDateStr === todayDateStr && !s.isCancelled;
          });
        }

        return true;
      })
      .sort((a: ScheduleEntry, b: ScheduleEntry) => {
        const aTime = a.startTime.split(':').map(Number);
        const bTime = b.startTime.split(':').map(Number);
        return aTime[0] * 60 + aTime[1] - (bTime[0] * 60 + bTime[1]);
      });
  }, [scheduleData, today]);

  // Get upcoming assignments from classes
  const upcomingAssignments = useMemo(() => {
    // We don't have the full assignment details in class list, just the count
    // We'll show the pending count per class
    return classes
      .filter((c) => c.pendingAssignments > 0)
      .slice(0, 3);
  }, [classes]);

  // Check if a class is happening now
  const getClassStatus = (entry: ScheduleEntry) => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentMinutes = currentHour * 60 + currentMinute;

    const [startH, startM] = entry.startTime.split(':').map(Number);
    const [endH, endM] = entry.endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (currentMinutes < startMinutes) {
      return { status: 'upcoming', label: 'À venir', color: 'bg-gray-100 text-gray-700' };
    } else if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
      return { status: 'ongoing', label: 'En cours', color: 'bg-blue-100 text-blue-700' };
    } else {
      return { status: 'completed', label: 'Terminé', color: 'bg-green-100 text-green-700' };
    }
  };

  const isLoading = isLoadingClasses || isLoadingSchedule;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Bonjour, {user?.firstName}
        </h1>
        <p className="text-gray-500">
          {format(today, "EEEE d MMMM yyyy", { locale: fr })}
        </p>
      </div>

      {/* Stats Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Courses */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BookOpen className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalClasses || 0}</p>
                <p className="text-sm text-gray-500">Cours inscrits</p>
              </div>
            </div>
          </div>

          {/* Average Attendance */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.avgAttendance || 0}%</p>
                <p className="text-sm text-gray-500">Taux de présence</p>
              </div>
            </div>
          </div>

          {/* Average Grade */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {(stats?.avgGrade ?? 0).toFixed(1)}/20
                </p>
                <p className="text-sm text-gray-500">Moyenne générale</p>
              </div>
            </div>
          </div>

          {/* Pending Assignments */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ClipboardList className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.pendingAssignments || 0}</p>
                <p className="text-sm text-gray-500">Devoirs à rendre</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Schedule - Main Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Today's Classes */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <h2 className="font-semibold text-gray-900">Cours d'aujourd'hui</h2>
              </div>
              <Link
                to={`/institution/${tenantId}/student/mon-emploi-du-temps`}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Voir l'emploi du temps
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {isLoadingSchedule ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : todayClasses.length === 0 ? (
              <div className="p-6 text-center">
                <Calendar className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                <p className="text-gray-500">Pas de cours prévu aujourd'hui</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {todayClasses.map((entry: ScheduleEntry, index: number) => {
                  const status = getClassStatus(entry);
                  const professorName = entry.professor
                    ? `${entry.professor.firstName} ${entry.professor.lastName}`
                    : 'Professeur';

                  return (
                    <div
                      key={entry.id || index}
                      className="px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-blue-600">
                              {entry.startTime} - {entry.endTime}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                          <h3 className="font-medium text-gray-900 truncate">
                            {entry.moduleElement?.name || 'Matière'}
                          </h3>
                          <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <User className="h-3.5 w-3.5" />
                              {professorName}
                            </span>
                            {entry.room && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                {entry.room.name}
                              </span>
                            )}
                            {entry.sessionType && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={{
                                  backgroundColor: `${entry.sessionType.color || '#3B82F6'}20`,
                                  color: entry.sessionType.color || '#3B82F6',
                                }}
                              >
                                {entry.sessionType.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* My Courses */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-blue-600" />
                <h2 className="font-semibold text-gray-900">Mes Cours</h2>
              </div>
              <Link
                to={`/institution/${tenantId}/student/mes-cours`}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Voir tous les cours
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {isLoadingClasses ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : classes.length === 0 ? (
              <div className="p-6 text-center">
                <BookOpen className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                <p className="text-gray-500">Aucun cours inscrit</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {classes.slice(0, 4).map((cls) => (
                  <div
                    key={`${cls.groupeId}-${cls.moduleElementId}`}
                    onClick={() => navigate(`/institution/${tenantId}/student/mes-cours/${cls.moduleElementId}`)}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">
                          {cls.moduleElementName}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">
                          {cls.professorName} • {cls.groupeName}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          cls.attendanceRate >= 80 ? 'bg-green-100 text-green-700' :
                          cls.attendanceRate >= 60 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {cls.attendanceRate}%
                        </span>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-900 mb-4">Accès rapide</h2>
            <div className="grid grid-cols-2 gap-2">
              <Link
                to={`/institution/${tenantId}/student/mes-cours`}
                className="flex flex-col items-center p-3 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
              >
                <BookOpen className="h-6 w-6 mb-1" />
                <span className="text-sm font-medium">Mes Cours</span>
              </Link>
              <Link
                to={`/institution/${tenantId}/student/mon-emploi-du-temps`}
                className="flex flex-col items-center p-3 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
              >
                <Calendar className="h-6 w-6 mb-1" />
                <span className="text-sm font-medium">Emploi du temps</span>
              </Link>
              <Link
                to={`/institution/${tenantId}/student/calendrier-academique`}
                className="flex flex-col items-center p-3 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
              >
                <CalendarDays className="h-6 w-6 mb-1" />
                <span className="text-sm font-medium">Calendrier</span>
              </Link>
              <Link
                to={`/institution/${tenantId}/student/compte-financier`}
                className="flex flex-col items-center p-3 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
              >
                <CreditCard className="h-6 w-6 mb-1" />
                <span className="text-sm font-medium">Paiements</span>
              </Link>
            </div>
          </div>

          {/* Finance Widget */}
          <FinanceWidget linkTo={`/institution/${tenantId}/student/compte-financier`} />

          {/* Pending Assignments */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-amber-600" />
                <h2 className="font-semibold text-gray-900">Devoirs à rendre</h2>
              </div>
            </div>

            {upcomingAssignments.length === 0 ? (
              <div className="p-4 text-center">
                <CheckCircle className="mx-auto h-8 w-8 text-green-400 mb-2" />
                <p className="text-sm text-gray-500">Aucun devoir en attente</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {upcomingAssignments.map((cls) => (
                  <div
                    key={cls.moduleElementId}
                    onClick={() => navigate(`/institution/${tenantId}/student/mes-cours/${cls.moduleElementId}`)}
                    className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {cls.moduleElementName}
                        </p>
                        <p className="text-xs text-amber-600">
                          {cls.pendingAssignments} devoir{cls.pendingAssignments > 1 ? 's' : ''} en attente
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attendance Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-purple-600" />
              <h2 className="font-semibold text-gray-900">Assiduité</h2>
            </div>

            {classes.length === 0 ? (
              <p className="text-sm text-gray-500 text-center">Aucune donnée disponible</p>
            ) : (
              <div className="space-y-3">
                {classes.slice(0, 3).map((cls) => (
                  <div key={cls.moduleElementId} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {cls.moduleElementName}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            cls.attendanceRate >= 80 ? 'bg-green-500' :
                            cls.attendanceRate >= 60 ? 'bg-amber-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${cls.attendanceRate}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700 w-10 text-right">
                        {cls.attendanceRate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
